import { useEffect, useState } from "react";
import { X, HeartStraight, CurrencyDollar } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";

/**
 * TipDrawer — opens on the artist profile; lets the logged-in user send a
 * one-off tip via Stripe Checkout. Amounts are defined by the backend
 * (/api/payments/pricing) so the UI is just a presenter.
 */
export default function TipDrawer({ artist, onClose }) {
  const [pricing, setPricing] = useState(null);
  const [pkg, setPkg] = useState("5");
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/pricing");
        setPricing(data);
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, []);

  const amounts = pricing ? Object.keys(pricing.tips) : ["1", "5", "20"];

  const send = async () => {
    setLoading(true);
    setError("");
    try {
      const body = { handle: artist.handle, origin: window.location.origin };
      if (pkg === "custom") {
        const v = parseFloat(custom);
        if (!v || v < 1) {
          setError("Introduce un monto válido (mínimo $1)");
          setLoading(false);
          return;
        }
        body.custom = v;
      } else {
        body.package = pkg;
      }
      const { data } = await api.post("/payments/tips/checkout", body);
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiError(e));
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="tip-drawer"
      className="fixed inset-0 z-[80] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full sm:max-w-md bg-[#050505] border border-[#27272A] max-h-[92vh] overflow-auto">
        <header className="sticky top-0 bg-[#050505] border-b border-[#27272A] p-5 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em]">
              // PROPINA AL ARTISTA
            </div>
            <h2 className="font-display font-black text-2xl uppercase tracking-tight mt-1">
              Tip @{artist.handle}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 border border-[#27272A] inline-flex items-center justify-center hover:border-[#FF0033] hover:text-[#FF0033]"
          >
            <X size={14} />
          </button>
        </header>

        <div className="p-5 space-y-5">
          <p className="font-mono text-[12px] text-[#A1A1AA] tracking-wider leading-relaxed">
            Apoya directamente el trabajo de{" "}
            <span className="text-white">{artist.name}</span>. 92% va al artista,
            8% a la plataforma.
          </p>

          <div className="grid grid-cols-4 gap-2">
            {amounts.map((a) => (
              <button
                key={a}
                data-testid={`tip-pkg-${a}`}
                onClick={() => setPkg(a)}
                className={`py-4 border font-display font-black text-lg transition-colors ${
                  pkg === a
                    ? "bg-[#FF3E00] text-white border-[#FF3E00]"
                    : "border-[#27272A] bg-[#0a0a0a] hover:border-[#F4F4F5]"
                }`}
              >
                ${a}
              </button>
            ))}
            <button
              data-testid="tip-pkg-custom"
              onClick={() => setPkg("custom")}
              className={`py-4 border font-mono text-[10px] uppercase tracking-[0.22em] transition-colors ${
                pkg === "custom"
                  ? "bg-[#FF3E00] text-white border-[#FF3E00]"
                  : "border-[#27272A] bg-[#0a0a0a] hover:border-[#F4F4F5]"
              }`}
            >
              Otro
            </button>
          </div>

          {pkg === "custom" ? (
            <div>
              <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em] mb-2">
                // MONTO PERSONALIZADO (USD)
              </div>
              <div className="flex items-center border border-[#27272A] focus-within:border-[#FF3E00]">
                <span className="h-12 w-12 inline-flex items-center justify-center border-r border-[#27272A] text-[#A1A1AA]">
                  <CurrencyDollar size={14} />
                </span>
                <input
                  type="number"
                  min="1"
                  max="500"
                  step="0.01"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder="10.00"
                  data-testid="tip-custom-input"
                  className="flex-1 bg-transparent focus:outline-none px-3 py-3 font-display font-bold text-xl"
                />
              </div>
              <div className="mt-1 font-mono text-[10px] text-[#525252] tracking-wider">
                MIN $1 · MAX $500
              </div>
            </div>
          ) : null}

          {error ? (
            <div className="border border-[#FF0033] bg-[#FF0033]/10 px-3 py-2 font-mono text-[11px] text-[#FF8899]">
              {error}
            </div>
          ) : null}

          <button
            data-testid="tip-send"
            onClick={send}
            disabled={loading}
            className={`btn-brutal w-full inline-flex items-center justify-center gap-2 font-mono uppercase text-xs tracking-[0.22em] font-bold py-4 ${
              loading
                ? "bg-[#121212] text-[#525252]"
                : "bg-[#FF3E00] text-white"
            }`}
          >
            <HeartStraight size={14} weight="fill" />
            {loading ? "Redirigiendo a Stripe…" : "Continuar con Stripe"}
          </button>

          <p className="font-mono text-[9px] text-[#525252] tracking-[0.22em] uppercase text-center">
            · Pago seguro · Stripe · Modo test ·
          </p>
        </div>
      </div>
    </div>
  );
}
